import * as XLSX from 'xlsx';

function dateStamp() {
  return new Date().toISOString().slice(0, 10);
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function num2(n) {
  if (n == null || Number.isNaN(n)) return '';
  return Number(n).toFixed(2);
}

/**
 * Opent een afdrukvoorbeurt; kies “Opslaan als PDF” / Print to PDF om het bestand te krijgen.
 */
function openPrintTable({ title, subtitleLines, columns, rows }) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('Pop-up geblokkeerd. Sta pop-ups toe voor deze site om de prijslijst te openen.');
    return;
  }
  const headRow = columns.map((c) => `<th>${esc(c)}</th>`).join('');
  const bodyRows = rows
    .map((cells) => `<tr>${cells.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`)
    .join('');
  const subHtml = (subtitleLines || []).map((line) => `<div class="sub-line">${esc(line)}</div>`).join('');

  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  body { font-family: system-ui, Segoe UI, sans-serif; margin: 24px; color: #222; }
  h1 { font-size: 18px; margin: 0 0 10px; }
  .sub { color: #555; font-size: 12px; margin-bottom: 16px; }
  .sub-line { margin-bottom: 4px; }
  table { border-collapse: collapse; width: 100%; font-size: 11px; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
  th { background: #e8f7f5; font-weight: 600; }
  .hint { margin-top: 20px; font-size: 11px; color: #666; max-width: 40em; }
  @media print { body { margin: 12px; } .hint { display: none; } }
</style></head><body>
<h1>${esc(title)}</h1>
<div class="sub">${subHtml}</div>
<table><thead><tr>${headRow}</tr></thead><tbody>${bodyRows}</tbody></table>
<p class="hint">Kies in het afdrukvenster <strong>Opslaan als PDF</strong> (of Microsoft Print to PDF) om een PDF-bestand te maken.</p>
<script>window.onload=function(){setTimeout(function(){window.print();},250)}<\/script>
</body></html>`);
  w.document.close();
}

/**
 * @param {Array<{ product: object, calc: object }>} rows
 * @param {object} settings
 */
export function downloadAdviesVerkoopPdf(rows, settings) {
  const btw = Math.max(0, parseFloat(settings?.btw_pct) || 0);
  const rond95 = settings?.rond_advies_incl_btw_op_punt95 !== false;
  const lines = [
    `Gegenereerd: ${new Date().toLocaleString('nl-NL')} · ${rows.length} product(en)`,
    btw > 0 ? `BTW consumentenadvies: ${btw}% (kolom “Advies incl. BTW”)` : null,
    rond95 ? 'Advies incl. BTW: afgerond naar het volgende bedrag op ,95, plus eventuele handmatige correctie (kolom Corr.)' : null,
    !rond95 ? 'Advies incl. BTW = berekend + handmatige correctie (kolom Corr.)' : null,
  ].filter(Boolean);

  const body = rows.map(({ product: p, calc }) => [
    String(p.naam || '').slice(0, 120),
    p.sku || '—',
    p.ean || '—',
    num2(calc.advies_verkoop_ex_btw_eur),
    String(Math.round(calc.advies_correctie_eur_incl_btw || 0)),
    num2(calc.advies_verkoop_incl_btw_eur),
    num2(calc.verkoop_woo),
  ]);

  openPrintTable({
    title: 'Advies verkoopprijslijst',
    subtitleLines: lines,
    columns: ['Naam', 'SKU', 'EAN', 'Advies ex BTW €', 'Corr. € incl.', 'Advies incl. BTW €', 'Verkoop Woo €'],
    rows: body,
  });
}

/**
 * @param {Array<{ product: object, calc: object }>} rows
 * @param {object} settings
 */
export function downloadDealerPdf(rows, settings) {
  const korting = Math.max(0, Math.min(100, parseFloat(settings?.dealer_korting_pct) || 0));
  const lines = [
    `Gegenereerd: ${new Date().toLocaleString('nl-NL')} · ${rows.length} product(en)`,
    `Dealerprijzen zijn excl. BTW. Dealerkorting: ${korting}% op advies ex BTW.`,
  ];

  const body = rows.map(({ product: p, calc }) => [
    String(p.naam || '').slice(0, 120),
    p.sku || '—',
    p.ean || '—',
    num2(calc.advies_verkoop_ex_btw_eur),
    num2(calc.dealer_prijs_ex_btw_eur),
  ]);

  openPrintTable({
    title: 'Dealerprijslijst (advies, excl. BTW)',
    subtitleLines: lines,
    columns: ['Naam', 'SKU', 'EAN', 'Advies ex BTW €', `Dealer ex BTW € (−${korting}%)`],
    rows: body,
  });
}

const KOST_VERKOOP_EXCEL_HEADERS = [
  'Naam',
  'SKU',
  'EAN',
  'Leverancier',
  'Valuta_inkoop',
  'Kostprijs_EUR',
  'Extra_EUR',
  'Advies_ex_BTW_EUR',
  'Advies_correctie_incl_EUR',
  'Bruto_marge_ex_BTW_EUR',
  'Advies_incl_BTW_EUR',
  'Dealer_ex_BTW_EUR',
  'Verkoop_Woo_EUR',
];

/**
 * @param {Array<{ product: object, calc: object }>} rows
 * @param {object} [settings]
 */
export function downloadKostEnVerkoopExcel(rows, settings) {
  const rond95 = settings?.rond_advies_incl_btw_op_punt95 !== false;
  const body = rows.map(({ product: p, calc }) => [
    p.naam || '',
    p.sku || '',
    p.ean || '',
    p.leverancier_naam || '',
    calc.valuta,
    roundExcel(calc.kostprijs_eur),
    roundExcel(calc.extra_per_stuk),
    roundExcel(calc.advies_verkoop_ex_btw_eur),
    calc.advies_correctie_eur_incl_btw != null && calc.advies_correctie_eur_incl_btw !== 0
      ? Math.round(calc.advies_correctie_eur_incl_btw)
      : 0,
    roundExcel(calc.marge_werkelijk_ex_btw_eur),
    roundExcel(calc.advies_verkoop_incl_btw_eur),
    roundExcel(calc.dealer_prijs_ex_btw_eur),
    roundExcel(calc.verkoop_woo),
  ]);
  const aoa = rond95
    ? [['Advies incl. BTW: afgerond naar het volgende bedrag op ,95'], KOST_VERKOOP_EXCEL_HEADERS, ...body]
    : [KOST_VERKOOP_EXCEL_HEADERS, ...body];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Prijzen');
  XLSX.writeFile(wb, `kost-en-verkoopprijzen-${dateStamp()}.xlsx`);
}

function roundExcel(n) {
  if (n == null || Number.isNaN(n)) return '';
  return Math.round(Number(n) * 100) / 100;
}
